<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="900px">
      <v-card>
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{ $t("wkt.admin.training-enrollment-results-batch.window.title") }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text @wheel="onWheel">
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="enrollmentIds"
                    :items="eligibleEnrollments"
                    :item-text="getEnrollmentText"
                    item-value="id"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.enrollments')"
                    multiple
                    chips
                    deletable-chips
                    :disabled="isReadOnly"
                    :menu-props="trainingEnrollmentResultBatchDialogMenuProps"
                    clearable
                    :rules="[validationRules.requiredMultiple]"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="applyStatus"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.apply-status')"
                    :disabled="isReadOnly"
                    hide-details
                    class="mt-0 pt-0"
                  />
                  <v-select
                    v-model="status"
                    :items="statusOptions"
                    item-text="name"
                    item-value="id"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.status')"
                    :disabled="isReadOnly || !applyStatus"
                    :menu-props="trainingEnrollmentResultBatchDialogMenuProps"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="applyResultDate"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.apply-result-date')"
                    :disabled="isReadOnly"
                    hide-details
                    class="mt-0 pt-0"
                  />
                  <v-menu ref="resultDateMenu" v-model="resultDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="resultDateFormatted"
                        :label="$t('wkt.admin.training-enrollment-results-batch.window.result-date')"
                        v-on="on"
                        v-bind="attrs"
                        readonly
                        :disabled="isReadOnly || !applyResultDate"
                        prepend-icon="mdi-calendar"
                      />
                    </template>
                    <v-date-picker v-model="resultDate" :first-day-of-week="1" :locale="$i18n.locale" no-title @input="resultDateMenu = false" />
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="applyResultValue"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.apply-result-value')"
                    :disabled="isReadOnly"
                    hide-details
                    class="mt-0 pt-0"
                  />
                  <v-text-field
                    v-model="resultValue"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.result-value')"
                    :disabled="isReadOnly || !applyResultValue"
                    maxlength="500"
                    counter="500"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="applyObservations"
                    :label="$t('wkt.admin.training-enrollment-results-batch.window.apply-observations')"
                    :disabled="isReadOnly"
                    hide-details
                    class="mt-0 pt-0"
                  />
                  <v-textarea
                    v-model="observations"
                    :label="$t('wkt.admin.training-enrollment.edit-window.observations')"
                    auto-grow
                    rows="3"
                    maxlength="2000"
                    counter="2000"
                    :disabled="isReadOnly || !applyObservations"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <span style="color: #757575; font-size: 13px;">
                    {{ $t("wkt.admin.training-enrollment-results-batch.window.note") }}
                  </span>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import { Constants } from "../../common/Constants.js";

export default {
  name: "TrainingEnrollmentResultBatchDialog",
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      isReadOnly: false,
      trainingAction: null,
      enrollments: [],
      trainingEnrollmentResultBatchDialogMenuProps: {
        zIndex: 260,
        contentClass: "training-enrollment-result-batch-dialog-menu",
      },
      enrollmentIds: [],
      applyStatus: true,
      status: Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED,
      applyResultValue: false,
      resultValue: "",
      applyResultDate: true,
      resultDate: null,
      resultDateMenu: false,
      resultDateFormatted: null,
      applyObservations: false,
      observations: "",
    };
  },
  computed: {
    statusOptions() {
      return [
        { id: Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED, name: this.$t("wkt.admin.training-enrollment.status.completed") },
        { id: Constants.TRAINING_ENROLLMENT_STATUS_NOT_PASS, name: this.$t("wkt.admin.training-enrollment.status.not-pass") },
        { id: Constants.TRAINING_ENROLLMENT_STATUS_NO_SHOW, name: this.$t("wkt.admin.training-enrollment.status.no-show") },
      ];
    },
    eligibleEnrollments() {
      return (this.enrollments || []).filter(
        (item) =>
          item
          && item.status !== Constants.TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL
          && item.status !== Constants.TRAINING_ENROLLMENT_STATUS_REJECTED
          && item.status !== Constants.TRAINING_ENROLLMENT_STATUS_CANCELED,
      );
    },
    hasChangesToApply() {
      return this.applyStatus || this.applyResultValue || this.applyResultDate || this.applyObservations;
    },
  },
  mounted() {
    this.installShadowMenuWheelPatch({
      menuSelector: ".training-enrollment-result-batch-dialog-menu",
      handlerKey: "__tweemTrainingEnrollmentResultBatchManualWheelHandler",
    });
  },
  beforeDestroy() {
    this.teardownShadowMenuWheelPatch();
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return;
      }

      event.stopPropagation();
    },
    open(payload) {
      this.showDialog = true;
      this.isReadOnly = !!payload?.isReadOnly;
      this.trainingAction = payload?.trainingAction || null;
      this.enrollments = payload?.enrollments || [];
      this.enrollmentIds = [];
      this.applyStatus = true;
      this.status = Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED;
      this.applyResultValue = false;
      this.resultValue = "";
      this.applyResultDate = true;
      this.resultDate = this.formatLocalDateToISOStringDate(new Date()).split(", ")[0];
      this.applyObservations = false;
      this.observations = "";

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    getEnrollmentText(item) {
      if (!item) {
        return "";
      }
      const fullName = `${item.employeeLastname || ""}, ${item.employeeFirstname || ""}`.replace(/^,\s*/, "");
      return `${fullName} - ${this.getEnrollmentStatusText(item.status)}`;
    },
    getEnrollmentStatusText(status) {
      switch (status) {
        case Constants.TRAINING_ENROLLMENT_STATUS_PENDING_APPROVAL:
          return this.$t("wkt.admin.training-enrollment.status.pending-approval");
        case Constants.TRAINING_ENROLLMENT_STATUS_PENDING_SELECTION:
          return this.$t("wkt.admin.training-enrollment.status.pending-selection");
        case Constants.TRAINING_ENROLLMENT_STATUS_ENROLLED:
          return this.$t("wkt.admin.training-enrollment.status.enrolled");
        case Constants.TRAINING_ENROLLMENT_STATUS_REJECTED:
          return this.$t("wkt.admin.training-enrollment.status.rejected");
        case Constants.TRAINING_ENROLLMENT_STATUS_COMPLETED:
          return this.$t("wkt.admin.training-enrollment.status.completed");
        case Constants.TRAINING_ENROLLMENT_STATUS_NOT_PASS:
          return this.$t("wkt.admin.training-enrollment.status.not-pass");
        case Constants.TRAINING_ENROLLMENT_STATUS_NO_SHOW:
          return this.$t("wkt.admin.training-enrollment.status.no-show");
        case Constants.TRAINING_ENROLLMENT_STATUS_CANCELED:
          return this.$t("wkt.admin.training-enrollment.status.canceled");
        default:
          return "";
      }
    },
    isoDateToSimpleDate(value) {
      return value ? this.convertDateToSimpleDate(new Date(`${value}`)) : null;
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }
      if (!this.hasChangesToApply) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.training-enrollment-results-batch.window.validation-at-least-one"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.$emit("save", {
        trainingActionId: this.trainingAction?.id || null,
        enrollmentIds: this.enrollmentIds,
        applyStatus: this.applyStatus,
        status: this.applyStatus ? this.status : null,
        applyResultValue: this.applyResultValue,
        resultValue: this.applyResultValue ? (this.resultValue || null) : null,
        applyResultDate: this.applyResultDate,
        resultDate: this.applyResultDate ? this.isoDateToSimpleDate(this.resultDate) : null,
        applyObservations: this.applyObservations,
        observations: this.applyObservations ? (this.observations || null) : null,
      });
      this.showDialog = false;
    },
  },
  watch: {
    resultDate(val) {
      this.resultDateFormatted = val ? this.formatISOStringDateToLocalDate(val) : null;
    },
  },
};
</script>
